import { CircleAlertIcon } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { FieldsetCard } from '@/components/atoms/FieldsetCard/FieldsetCard';
import { MultiSelectField } from '@/components/atoms/MultiSelect';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { useLiveChat } from '@/hooks/useLiveChat';
import { LiveChatIntegration } from '@/shared/types/integration';

import { CardBaseHeader } from '../Components/CardBase';

interface Props {
  integration: LiveChatIntegration;
  canWrite: boolean;
}

type GroupForm = { group_ids: string[] };

const FORM_ID = 'livechat-group-form';

const GroupCard = ({ integration, canWrite }: Props) => {
  const { t } = useTranslation();
  const { groupOptions, groupIds, botStatus, saveGroups, saveGroupsStatus } = useLiveChat(
    integration.desk_id,
    integration.integration_id
  );

  const form = useForm<GroupForm>({ values: { group_ids: groupIds.map(String) } });

  const {
    control,
    handleSubmit,
    formState: { isDirty },
  } = form;

  const onSubmit = ({ group_ids }: GroupForm) => saveGroups(group_ids.map(Number));

  return (
    <div className="w-full">
      <CardBaseHeader title={t('integrations.livechat.group.title')} titleVariant="secondary" />
      <Form {...form}>
        <form id={FORM_ID} onSubmit={handleSubmit(onSubmit)}>
          <FieldsetCard
            isDirty={isDirty}
            isLoading={saveGroupsStatus === 'pending'}
            disabled={!isDirty || !canWrite}
            saveButtonLabel={t('common.save')}
            formId={FORM_ID}
          >
            {botStatus === 'success' && groupIds.length === 0 && (
              <div className="flex items-center gap-2 rounded-md bg-warning/10 px-3 py-2 text-warning">
                <CircleAlertIcon className="h-4 w-4 shrink-0" />
                <p className="text-sm">{t('integrations.livechat.group.no_group')}</p>
              </div>
            )}
            {botStatus === 'error' ? (
              <p role="alert" className="text-sm text-destructive">
                {t('integrations.livechat.load_error')}
              </p>
            ) : (
              <FormField
                control={control}
                name="group_ids"
                rules={{ required: t('common.required_field') }}
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('integrations.livechat.group.label')}</FormLabel>
                    <FormControl>
                      <MultiSelectField
                        title={t('integrations.livechat.group.placeholder')}
                        options={groupOptions}
                        value={field.value}
                        onChange={field.onChange}
                        disabled={botStatus === 'pending'}
                      />
                    </FormControl>
                    <FormDescription>{t('integrations.livechat.group.subtitle')}</FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}
          </FieldsetCard>
        </form>
      </Form>
    </div>
  );
};

export default GroupCard;
